import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import CommercialNotice from "../components/CommercialNotice";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { Sparkles, RefreshCw, Lock, ArrowUpRight, Target, TrendingUp, Award, DollarSign, Compass, ListChecks, Calendar, Zap, ChevronRight } from "lucide-react";

const SUB_SCORE_LABELS = {
  profile_completeness: "Profile completeness",
  audience_quality: "Audience quality",
  content_cadence: "Content cadence",
  brand_alignment: "Brand alignment",
  narrative_clarity: "Narrative clarity",
};

export default function Positioning() {
  const { user } = useAuth();
  const plan = (user?.plan || "free").toLowerCase();
  const isPro = plan === "pro" || plan === "agency";
  const [report, setReport] = useState(null);
  const [createdAt, setCreatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!isPro) { setLoading(false); return; }
    api.get("/ai/positioning/latest").then(({ data }) => {
      if (data.exists) { setReport(data.report); setCreatedAt(data.created_at); }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [isPro]);

  const generate = async () => {
    setGenerating(true); setErr("");
    try {
      const { data } = await api.post("/ai/positioning", {}, { timeout: 180000 });
      setReport(data);
      setCreatedAt(new Date().toISOString());
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || (e.code === "ECONNABORTED" ? "Report took too long. Try again — your data is being saved server-side." : e.message));
    } finally { setGenerating(false); }
  };

  if (!isPro) return (
    <DashboardLayout>
      <div className="p-6 md:p-12 max-w-5xl" data-testid="positioning-paywall">
        <div className="label-eyebrow">Pro feature</div>
        <h1 className="font-heading text-4xl md:text-6xl uppercase tracking-tighter mt-2 leading-[0.95]">
          Become <span className="text-volt">sponsorable.</span>
        </h1>
        <p className="text-ink-muted text-lg mt-4 max-w-2xl">
          The AI Positioning Engine analyses your sport, audience, content style, and achievements to produce a 5-section strategic report — your readiness score, athlete archetype, monetisation playbook, and an 8-week content strategy.
        </p>
        <div className="surface p-8 mt-10 border-volt/40 bg-volt/5">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-volt text-black flex items-center justify-center flex-shrink-0">
              <Lock className="w-6 h-6" strokeWidth={2.5} />
            </div>
            <div className="flex-1">
              <h2 className="font-heading text-2xl uppercase tracking-tight">Unlock with Pro</h2>
              <p className="text-ink-muted text-sm mt-2 leading-relaxed">
                Get the report top agents charge £500+ for, generated in 2 minutes from your existing profile.
              </p>
              <div className="mt-6 grid sm:grid-cols-3 gap-3">
                <Tile icon={<TrendingUp className="w-4 h-4 text-volt" />} title="Readiness Score" desc="0-100, with 5 sub-scores diagnosing exactly where you need to improve" />
                <Tile icon={<Award className="w-4 h-4 text-volt" />} title="Your Archetype" desc="Which of 8 sponsor-ready athlete archetypes you are — and what brands look for" />
                <Tile icon={<Calendar className="w-4 h-4 text-volt" />} title="8-Week Content Plan" desc="Specific weekly themes proven to lift sponsorship intent from brands" />
              </div>
              <Link to="/app/pricing" className="btn-volt inline-flex items-center gap-2 mt-6" data-testid="positioning-upgrade-btn">
                Upgrade to Pro <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
        <CommercialNotice />
      </div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div className="p-6 md:p-12 max-w-6xl" data-testid="positioning-root">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="label-eyebrow inline-flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-volt" /> AI Positioning Engine</div>
            <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-tighter mt-2">Your sponsorship readiness</h1>
            <p className="text-ink-muted text-sm md:text-base mt-2 max-w-2xl">A strategic report on your archetype, monetisation paths, improvement plan, and 8-week content strategy — generated from your full PITCHD profile.</p>
          </div>
          <button onClick={generate} disabled={generating} className="btn-volt inline-flex items-center gap-2 disabled:opacity-50" data-testid="generate-positioning-btn">
            {generating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {generating ? "Analysing… (~90s)" : report ? "Regenerate report" : "Generate my report"}
          </button>
        </div>

        {err && <div className="surface p-4 border-[#FF3B30]/50 text-[#FF3B30] text-sm mb-6" data-testid="positioning-err">{err}</div>}

        {loading && !report && <div className="label-eyebrow">Loading…</div>}

        {!loading && !report && !generating && (
          <div className="surface p-12 border-dashed text-center" data-testid="positioning-empty">
            <div className="w-14 h-14 border border-volt text-volt flex items-center justify-center mx-auto mb-5"><Compass className="w-6 h-6" /></div>
            <h3 className="font-heading text-2xl uppercase tracking-tight mb-2">Get your first Positioning Report</h3>
            <p className="text-sm text-ink-muted max-w-md mx-auto mb-6">Takes about 90 seconds. Uses your sport, audience, achievements, and media kit data to produce a full strategic playbook.</p>
            <button onClick={generate} className="btn-volt inline-flex items-center gap-2" data-testid="generate-empty-btn">
              <Sparkles className="w-4 h-4" /> Generate now
            </button>
          </div>
        )}

        {generating && !report && (
          <div className="surface p-12 text-center" data-testid="positioning-generating">
            <RefreshCw className="w-8 h-8 text-volt animate-spin mx-auto mb-4" />
            <div className="font-heading text-xl uppercase tracking-tight">Analysing your profile…</div>
            <div className="text-xs text-ink-muted mt-2">This takes ~90 seconds. We're scoring 5 dimensions, identifying your archetype, and writing your 8-week content strategy.</div>
          </div>
        )}

        {report && <Report report={report} createdAt={createdAt} />}
      </div>
    </DashboardLayout>
  );
}

function Tile({ icon, title, desc }) {
  return (
    <div className="border border-line p-4">
      {icon}
      <div className="font-heading text-sm uppercase tracking-tight mt-2">{title}</div>
      <div className="text-xs text-ink-muted leading-relaxed mt-1">{desc}</div>
    </div>
  );
}

function Report({ report, createdAt }) {
  const subScores = report.sub_scores || {};
  return (
    <div className="space-y-8" data-testid="positioning-report">
      {/* TOP: Score + Archetype */}
      <div className="grid md:grid-cols-2 gap-6">
        <ScoreCard score={report.readiness_score} subScores={subScores} />
        <ArchetypeCard archetype={report.archetype} positioning={report.positioning_statement} />
      </div>

      {/* Best fit categories */}
      {report.best_fit_categories?.length > 0 && (
        <section data-testid="report-categories">
          <SectionHeader icon={<Target className="w-3.5 h-3.5" />} title="Best-fit sponsor categories" subtitle="Where to focus your outreach effort first" />
          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            {report.best_fit_categories.map((c, i) => (
              <div key={i} className="surface p-5 hover:border-volt/40 transition-colors" data-testid={`category-${i}`}>
                <div className="flex justify-between items-start gap-3 mb-2">
                  <h3 className="font-heading text-lg uppercase tracking-tight">{c.category}</h3>
                  <div className="font-mono text-base text-volt whitespace-nowrap">{c.confidence}/100</div>
                </div>
                <p className="text-sm text-ink-muted leading-relaxed">{c.reason}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Monetisation */}
      {report.monetisation_opportunities?.length > 0 && (
        <section data-testid="report-monetisation">
          <SectionHeader icon={<DollarSign className="w-3.5 h-3.5" />} title="Monetisation opportunities" subtitle="3 paths to revenue beyond one-off posts" />
          <div className="grid md:grid-cols-3 gap-3 mt-4">
            {report.monetisation_opportunities.map((m, i) => (
              <div key={i} className="surface p-5" data-testid={`monetisation-${i}`}>
                <div className="text-[10px] uppercase tracking-widest text-volt mb-2">Opportunity {i + 1}</div>
                <h3 className="font-heading text-lg uppercase tracking-tight mb-2">{m.title}</h3>
                <p className="text-sm text-ink-muted leading-relaxed mb-4">{m.description}</p>
                <div className="border-t border-line pt-3 text-xs">
                  <span className="text-ink-dim uppercase tracking-widest text-[10px]">Estimated revenue</span>
                  <div className="text-volt font-mono mt-1">{m.est_revenue_range}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Improvement plan */}
      {report.improvement_plan?.length > 0 && (
        <section data-testid="report-improvements">
          <SectionHeader icon={<ListChecks className="w-3.5 h-3.5" />} title="Improvement plan" subtitle="Specific actions to raise your readiness score" />
          <div className="surface mt-4 divide-y divide-line">
            {report.improvement_plan.map((p, i) => (
              <div key={i} className="p-5 flex items-start gap-4" data-testid={`improvement-${i}`}>
                <div className={`w-8 h-8 flex items-center justify-center text-[10px] font-mono uppercase border ${impactStyle(p.impact)}`}>
                  {p.impact?.charAt(0) || "·"}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="label-eyebrow text-[10px]">{p.area}</span>
                    <span className={`text-[9px] uppercase tracking-widest font-semibold ${impactTextStyle(p.impact)}`}>{p.impact}</span>
                  </div>
                  <p className="text-sm text-white leading-relaxed">{p.action}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Content strategy */}
      {report.content_strategy?.weekly_plan?.length > 0 && (
        <section data-testid="report-content-strategy">
          <SectionHeader icon={<Calendar className="w-3.5 h-3.5" />} title="8-week content strategy" subtitle={report.content_strategy.thesis} />
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3 mt-4">
            {report.content_strategy.weekly_plan.map((w, i) => (
              <div key={i} className="surface p-4" data-testid={`week-${w.week}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 bg-volt text-black flex items-center justify-center text-[10px] font-mono font-semibold">{w.week}</div>
                  <div className="font-heading text-sm uppercase tracking-tight">Week {w.week}</div>
                </div>
                <div className="text-volt text-xs font-semibold mb-2">{w.theme}</div>
                <ul className="space-y-1 text-xs text-ink-muted">
                  {(w.post_types || []).map((pt, j) => (
                    <li key={j} className="flex gap-1.5"><ChevronRight className="w-3 h-3 text-volt flex-shrink-0 mt-0.5" /><span>{pt}</span></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      )}

      {createdAt && (
        <div className="text-[10px] text-ink-dim font-mono uppercase tracking-widest text-center pt-6 border-t border-line">
          Generated {new Date(createdAt).toLocaleString()}
        </div>
      )}
    </div>
  );
}

function ScoreCard({ score, subScores }) {
  const tier = score >= 80 ? { label: "Sponsor-ready", cls: "text-volt" } : score >= 60 ? { label: "Close — sharpen", cls: "text-volt" } : score >= 40 ? { label: "Building momentum", cls: "text-amber-400" } : { label: "Early days", cls: "text-[#FF8B30]" };
  return (
    <div className="surface p-6" data-testid="score-card">
      <div className="label-eyebrow mb-2">Sponsorship readiness</div>
      <div className="flex items-baseline gap-3">
        <div className="font-heading text-6xl md:text-7xl text-volt tracking-tighter" data-testid="readiness-score">{score ?? "—"}</div>
        <div className="text-ink-muted text-sm font-mono">/100</div>
      </div>
      <div className={`text-xs uppercase tracking-widest mt-1 ${tier.cls}`}>{tier.label}</div>
      <div className="mt-6 space-y-3">
        {Object.entries(SUB_SCORE_LABELS).map(([key, label]) => {
          const v = subScores[key] ?? 0;
          return (
            <div key={key} data-testid={`subscore-${key}`}>
              <div className="flex justify-between items-center text-xs mb-1">
                <span className="text-ink-muted">{label}</span>
                <span className="font-mono text-white">{v}</span>
              </div>
              <div className="h-1 bg-bg-elev overflow-hidden">
                <div className="h-full bg-volt transition-all" style={{ width: `${v}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ArchetypeCard({ archetype, positioning }) {
  if (!archetype) return null;
  return (
    <div className="surface p-6 border-volt/40 bg-volt/5" data-testid="archetype-card">
      <div className="label-eyebrow mb-2 inline-flex items-center gap-2"><Zap className="w-3 h-3 text-volt" /> Your archetype</div>
      <h2 className="font-heading text-3xl md:text-4xl uppercase tracking-tighter leading-[0.95] mt-1">{archetype.name}</h2>
      <p className="text-volt text-sm italic mt-3 font-semibold">"{archetype.tagline}"</p>
      <p className="text-sm text-ink-muted leading-relaxed mt-4">{archetype.description}</p>
      {positioning && (
        <div className="mt-6 border-l-2 border-volt pl-4">
          <div className="label-eyebrow text-[10px] mb-1">Your positioning statement</div>
          <p className="text-base text-white leading-relaxed">{positioning}</p>
        </div>
      )}
    </div>
  );
}

function SectionHeader({ icon, title, subtitle }) {
  return (
    <div>
      <div className="label-eyebrow inline-flex items-center gap-2">{icon}{title}</div>
      <h2 className="font-heading text-2xl md:text-3xl uppercase tracking-tighter mt-1">{title}</h2>
      {subtitle && <p className="text-sm text-ink-muted mt-1 italic">{subtitle}</p>}
    </div>
  );
}

function impactStyle(impact) {
  if (impact === "High") return "border-volt text-volt bg-volt/10";
  if (impact === "Medium") return "border-amber-400 text-amber-400";
  return "border-line text-ink-muted";
}
function impactTextStyle(impact) {
  if (impact === "High") return "text-volt";
  if (impact === "Medium") return "text-amber-400";
  return "text-ink-muted";
}
